import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subject, combineLatest } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AccountCardComponent } from '../../shared/components/account-card/account-card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { UserFacade } from '../../core/store/user/user.facade';
import { User } from '../../core/models/user.model';
import { LoginData } from '../../core/models/login.model';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { AuthService } from '../../core/services/auth.service';
import { shouldAccessPendingOrders } from '../../core/utils/pending-orders-eligibility.util';
import {
  filterPendingOrdersForDisplay,
  resolvePendingOrdersDocument,
} from '../../core/utils/pending-order.util';
import { PendingOrdersService } from '../../core/services/pending-orders.service';

@Component({
  selector: 'umkt-my-account',
  standalone: true,
  imports: [RouterModule, AccountCardComponent, IconComponent, I18nPipe],
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.scss'],
})
export class MyAccountComponent implements OnInit, OnDestroy {
  private static readonly PENDING_ORDERS_COUNT_PAGE_SIZE = 100;

  userId = '';
  plansRoute = '';
  ordersRoute = '';
  pendingOrdersRoute = '/my-account/pending-orders';
  showPendingOrdersCard = false;
  pendingOrdersCount = 0;
  greetingName = '';

  private destroy$ = new Subject<void>();
  private userFacade = inject(UserFacade);
  private authService = inject(AuthService);
  private pendingOrdersService = inject(PendingOrdersService);

  ngOnInit(): void {
    this.userFacade.getUserDetails().subscribe((user: User | null) => {
      this.greetingName = this.resolveGreetingName(user);
      if (user?.uuid) {
        this.userId = user.uuid;
        this.plansRoute = '/my-account/plans';
        this.ordersRoute = '/my-account/orders';
      } else {
        this.userFacade.getLoginData().subscribe(loginData => {
          if (loginData?.userUuid) {
            this.userId = loginData.userUuid;
            this.plansRoute = '/my-account/plans';
            this.ordersRoute = '/my-account/orders';
          }
        });
      }
    });

    this.userFacade.getUserUuid().subscribe(uuid => {
      if (uuid && !this.plansRoute) {
        this.userId = uuid;
        this.plansRoute = '/my-account/plans';
        this.ordersRoute = '/my-account/orders';
      }
    });

    combineLatest([
      this.userFacade.getLoginData().pipe(take(1)),
      this.userFacade.getUserDetails().pipe(take(1)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([loginData, user]) => {
        this.showPendingOrdersCard = shouldAccessPendingOrders(
          loginData,
          this.authService
        );

        if (!this.showPendingOrdersCard) {
          this.pendingOrdersCount = 0;
          return;
        }

        this.loadPendingOrdersCount(user, loginData);
      });
  }

  private loadPendingOrdersCount(
    user: User | null,
    loginData: LoginData | null
  ): void {
    const document = resolvePendingOrdersDocument(user, loginData);
    if (!document) {
      this.pendingOrdersCount = 0;
      return;
    }

    const pageSize = MyAccountComponent.PENDING_ORDERS_COUNT_PAGE_SIZE;

    this.pendingOrdersService
      .getRequestsOrders(document, { page: 0, size: pageSize })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const content = response.content ?? [];
          const visible = filterPendingOrdersForDisplay(content);
          const totalElements = response.page?.totalElements ?? 0;

          if (totalElements <= pageSize) {
            this.pendingOrdersCount = visible.length;
            return;
          }

          const removedOnPage = content.length - visible.length;
          this.pendingOrdersCount = Math.max(0, totalElements - removedOnPage);
        },
        error: () => {
          this.pendingOrdersCount = 0;
        },
      });
  }

  private resolveGreetingName(user: User | null): string {
    const firstName = user?.firstName?.trim();
    if (firstName) {
      return firstName;
    }

    return user?.displayName?.trim() ?? '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
